import { FC, useEffect, useMemo, useState } from "react"
import cx from "classnames"
import { useParams } from "react-router-dom"
import Button from "@/components/Button"
import Checkbox from "@/components/Checkbox"
import ErrorMessage from "@/components/ErrorMessage"
import Loader from "@/components/Loader"
import OutlineButton from "@/components/OutlineButton"
import Select from "@/components/Select"
import { filterOption } from "@/components/Select/constants"
import {
  useCreateSequenceSpamTestMutation,
  useGenerateNextSequenceSpamTestVariationMutation,
  useGetSequenceSpamTestOptionsQuery,
  useGetSequenceSpamTestQuery,
  useListSequenceSpamTestsQuery,
  useTestSequenceSpamTestMutation,
} from "@/redux/services/sequencesApi"
import { useGetInboxingDomainsQuery } from "@/redux/services/domainsApi"
import { selectedDomainsSendCapacity } from "@/utils/domainCapacity"
import { downloadCsvString } from "@/utils/csv"
import { runCsvFileName, runVariationsToCsv } from "./csv"
import type {
  SequenceAPIResponse,
  SequenceSpamTestCopy,
  SequenceSpamTestRecipientProvider,
  SequenceSpamTestRun,
  SequenceSpamTestSummary,
  SequenceSpamTestVariant,
} from "@/types/entities/sequence"

type Props = {
  className?: string
  sequence: SequenceAPIResponse
}

type CountKey = "inbox" | "spam" | "missing" | "failed" | "pending" | "total"
type CountSummary = Record<CountKey, number>

type PendingAction = "generate" | "test" | null

const providerOptions: {
  label: string
  value: SequenceSpamTestRecipientProvider
}[] = [
  { label: "Google", value: "google" },
  { label: "Microsoft", value: "microsoft" },
]

const countKeys: CountKey[] = [
  "inbox",
  "spam",
  "missing",
  "failed",
  "pending",
  "total",
]

const countLabels: Record<CountKey, string> = {
  inbox: "Inbox",
  spam: "Spam",
  missing: "Missing",
  failed: "Failed",
  pending: "Pending",
  total: "Total",
}

const pendingStatuses = [
  "pending",
  "queued",
  "running",
  "processing",
  "in_progress",
]
const maxGenerateAttempts = 3
const generateRetryDelayMs = 500

const compactStrings = (values: (string | undefined | null | false)[]) =>
  values.filter((value): value is string => Boolean(value))

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms)
  })

const spamTestRunId = (run?: SequenceSpamTestRun) =>
  run?._id || run?.id || run?.runId

const spamTestRunTime = (run: SequenceSpamTestRun) =>
  new Date(run.updatedAt || run.createdAt || 0).getTime()

const latestSpamTestRun = (runs: SequenceSpamTestRun[]) =>
  [...runs].sort((a, b) => spamTestRunTime(b) - spamTestRunTime(a))[0]

const spamTestSummary = (
  source: SequenceSpamTestSummary = {},
): CountSummary => {
  const inbox = source.inbox ?? 0
  const spam = source.spam ?? 0
  const missing = source.missing ?? 0
  const failed = source.failed ?? 0
  const pending = source.pending ?? 0
  const total = source.total ?? inbox + spam + missing + failed + pending

  return {
    inbox,
    spam,
    missing,
    failed,
    pending,
    total,
  }
}

const spamTestRunSummary = (run?: SequenceSpamTestRun): CountSummary => {
  const source = spamTestSummary(
    run?.summary || run?.counts || run?.resultCounts,
  )
  const inbox = run?.inbox ?? source.inbox
  const spam = run?.spam ?? source.spam
  const missing = run?.missing ?? source.missing
  const failed = run?.failed ?? source.failed
  const pending = run?.pending ?? source.pending
  const total =
    run?.total ?? source.total ?? inbox + spam + missing + failed + pending

  return {
    inbox,
    spam,
    missing,
    failed,
    pending,
    total,
  }
}

const spamTestRunPending = (run?: SequenceSpamTestRun) => {
  const summary = spamTestRunSummary(run)
  const status = run?.status?.toLowerCase()
  const mode = run?.mode?.toLowerCase()

  if (mode === "generate_only") return false

  return Boolean(
    summary.pending > 0 || (status && pendingStatuses.includes(status)),
  )
}

const spamTestRunVariations = (run?: SequenceSpamTestRun) =>
  run?.variants?.length ? run.variants : run?.variations || []

const copyText = (copy?: SequenceSpamTestCopy) =>
  copy
    ? compactStrings([copy.greeting, copy.body, copy.signoff, copy.unsub]).join(
        "\n\n",
      )
    : ""

const variantPreview = (variant: SequenceSpamTestVariant) => {
  const preview =
    variant.copyPreview ||
    variant.preview ||
    (typeof variant.generatedCopy === "string" ? variant.generatedCopy : "")

  if (preview) {
    return preview
  }

  if (typeof variant.generatedCopy === "object") {
    return copyText(variant.generatedCopy)
  }

  if (variant.copy) {
    return copyText(variant.copy)
  }

  return compactStrings([
    variant.greeting,
    variant.body,
    variant.signoff,
    variant.unsubscribe || variant.unsubs?.[0],
  ]).join("\n\n")
}

const runDomainNames = (run?: SequenceSpamTestRun) => {
  const names = (run?.domains ?? [])
    .map((domain) => domain?.name)
    .filter((name): name is string => Boolean(name))
  if (names.length) return names

  const single = run?.domain
  if (single && typeof single === "object" && single.name) return [single.name]
  return []
}

const formatRunTimestamp = (run: SequenceSpamTestRun) => {
  const raw = run.updatedAt || run.createdAt
  if (!raw) return ""
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const runHistoryLabel = (run: SequenceSpamTestRun) => {
  const isTest = run.mode === "test_on_seeds" || Boolean(run.testSeeds)
  const kind = isTest ? "Test" : "Generated"
  const timestamp = formatRunTimestamp(run)
  const domains = runDomainNames(run)
  const domainNote = domains.length ? ` · ${domains.join(", ")}` : ""
  return compactStrings([
    `${kind}${timestamp ? ` · ${timestamp}` : ""}${domainNote}`,
  ]).join("")
}

const formatChangedFields = (fields: string[] = []) =>
  fields.length ? fields.join(", ") : "control"

const SequenceSpamTestPanel: FC<Props> = ({ className, sequence }: Props) => {
  const { id: orgId = "" } = useParams()
  const [domainIds, setDomainIds] = useState<string[]>([])
  const [emailIndex, setEmailIndex] = useState(0)
  const [versionIndex, setVersionIndex] = useState(0)
  const [recipientProvider, setRecipientProvider] =
    useState<SequenceSpamTestRecipientProvider>("google")
  const [selectedRunId, setSelectedRunId] = useState("")
  const [pollRuns, setPollRuns] = useState(false)
  const [pollRun, setPollRun] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const [generateProgress, setGenerateProgress] = useState<{
    done: number
    total: number
  } | null>(null)

  const {
    data: inboxingDomainsData,
    isLoading: domainsLoading,
    isFetching: domainsFetching,
  } = useGetInboxingDomainsQuery({ orgId, folderOnly: true }, { skip: !orgId })
  const domains = useMemo(
    () => inboxingDomainsData?.items ?? [],
    [inboxingDomainsData?.items],
  )

  const {
    data: spamTestRunsData,
    isLoading: runsLoading,
    isFetching: runsFetching,
  } = useListSequenceSpamTestsQuery(
    { id: sequence._id },
    {
      pollingInterval: pollRuns ? 5000 : 0,
      skip: !sequence._id,
    },
  )
  const spamTestRuns = useMemo(
    () => spamTestRunsData?.items ?? [],
    [spamTestRunsData?.items],
  )
  const {
    data: spamTestOptions,
    isLoading: optionsLoading,
    isFetching: optionsFetching,
  } = useGetSequenceSpamTestOptionsQuery(
    { id: sequence._id },
    { skip: !sequence._id },
  )
  const latestRun = useMemo(
    () => latestSpamTestRun(spamTestRuns),
    [spamTestRuns],
  )
  const latestRunId = spamTestRunId(latestRun) || ""
  const validRunIdSet = useMemo(
    () =>
      new Set(
        spamTestRuns
          .map((run) => spamTestRunId(run))
          .filter((runId): runId is string => Boolean(runId)),
      ),
    [spamTestRuns],
  )
  const activeRunId = selectedRunId || latestRunId

  const {
    data: selectedRun,
    isLoading: runLoading,
    isFetching: runFetching,
  } = useGetSequenceSpamTestQuery(
    { id: sequence._id, runId: activeRunId },
    {
      pollingInterval: pollRun || pollRuns ? 5000 : 0,
      skip: !sequence._id || !activeRunId,
    },
  )
  const [createSpamTest, { isLoading: createLoading }] =
    useCreateSequenceSpamTestMutation()
  const [generateNext, { isLoading: generateNextLoading }] =
    useGenerateNextSequenceSpamTestVariationMutation()
  const [testSpamTest, { isLoading: testLoading }] =
    useTestSequenceSpamTestMutation()

  const selectedDomains = useMemo(
    () => domains.filter(({ _id }) => domainIds.includes(_id)),
    [domainIds, domains],
  )
  const selectedDomainsCapacity = useMemo(
    () => selectedDomainsSendCapacity(selectedDomains),
    [selectedDomains],
  )
  const variationRounds = useMemo(
    () => spamTestOptions?.rounds ?? [],
    [spamTestOptions?.rounds],
  )
  const allVariantIds = useMemo(
    () =>
      variationRounds.flatMap((round) =>
        round.variations.map((variation) => variation.variantId),
      ),
    [variationRounds],
  )
  const optionSignature = allVariantIds.join("|")
  const selectedVariantIdSet = useMemo(
    () => new Set(selectedVariantIds),
    [selectedVariantIds],
  )
  const selectedVariationCount = selectedVariantIds.length
  const seedsPerVariation = spamTestOptions?.seedsPerVariation || 0
  const estimatedSeedSends =
    domainIds.length * selectedVariationCount * seedsPerVariation
  const selectedEmail = sequence.emails[emailIndex]
  const selectedVersion = selectedEmail?.versions[versionIndex]
  const activeRun =
    selectedRun ||
    spamTestRuns.find((run) => spamTestRunId(run) === activeRunId) ||
    latestRun
  const activeRunSummary = spamTestRunSummary(activeRun)
  const activeRunVariations = spamTestRunVariations(activeRun)
  const activeRunDomainNames = runDomainNames(activeRun)
  const activeRunIsTest =
    activeRun?.mode === "test_on_seeds" || Boolean(activeRun?.testSeeds)
  const activeRunStatus =
    activeRun?.status ||
    (activeRunVariations.length
      ? activeRunIsTest
        ? "testing"
        : "generated"
      : "not started")
  const copyPreview = useMemo(() => {
    if (!selectedVersion) {
      return ""
    }

    return compactStrings([
      selectedVersion.subjects[0]
        ? `Subject: ${selectedVersion.subjects[0]}`
        : undefined,
      selectedVersion.greetings[0],
      selectedVersion.body,
      selectedVersion.signoffs[0],
      selectedVersion.unsubs?.[0],
    ]).join("\n\n")
  }, [selectedVersion])
  const actionsBusy =
    createLoading ||
    generateNextLoading ||
    testLoading ||
    Boolean(pendingAction)
  const disableGenerate =
    !domainIds.length ||
    !selectedVersion ||
    actionsBusy ||
    optionsLoading ||
    !selectedVariationCount
  // Derive the test target from activeRunId (source of truth) rather than the
  // RTK Query run object, which can lag behind while a new run fetch is in
  // flight after switching run history. This prevents testing the wrong run.
  const testRunId = activeRunId
  const activeRunQueued = activeRun?.status?.toLowerCase() === "queued"
  const disableTest =
    !domainIds.length ||
    !testRunId ||
    actionsBusy ||
    activeRunQueued ||
    pendingAction === "generate"

  useEffect(() => {
    setEmailIndex(0)
    setVersionIndex(0)
    setSelectedRunId("")
    setSelectedVariantIds([])
    setDomainIds([])
    setPollRuns(false)
    setPollRun(false)
    setErrorMessage("")
    setGenerateProgress(null)
  }, [sequence._id])

  useEffect(() => {
    const firstRoundIds =
      variationRounds[0]?.variations.map((variation) => variation.variantId) ??
      []
    setSelectedVariantIds(firstRoundIds)
  }, [optionSignature, sequence._id, variationRounds])

  useEffect(() => {
    if (emailIndex !== 0) {
      setEmailIndex(0)
    }
  }, [emailIndex])

  useEffect(() => {
    if (!domains.length) {
      setDomainIds((current) => (current.length ? [] : current))
      return
    }

    const validIds = new Set(domains.map(({ _id }) => _id))
    setDomainIds((current) => {
      const pruned = current.filter((id) => validIds.has(id))
      if (!pruned.length) return [domains[0]._id]
      return pruned.length === current.length ? current : pruned
    })
  }, [domains])

  useEffect(() => {
    const versionsLength = sequence.emails[emailIndex]?.versions.length || 0

    if (!versionsLength || versionIndex >= versionsLength) {
      setVersionIndex(0)
    }
  }, [emailIndex, sequence.emails, versionIndex])

  useEffect(() => {
    if (!latestRunId) {
      if (selectedRunId) {
        setSelectedRunId("")
      }
      return
    }

    if (!selectedRunId || !validRunIdSet.has(selectedRunId)) {
      setSelectedRunId(latestRunId)
    }
  }, [latestRunId, selectedRunId, validRunIdSet])

  useEffect(() => {
    setPollRuns(spamTestRunPending(latestRun))
  }, [latestRun])

  useEffect(() => {
    setPollRun(spamTestRunPending(activeRun))
  }, [activeRun])

  const handleToggleRound = (roundNumber: number, checked: boolean) => {
    const round = variationRounds.find(
      (candidate) => candidate.roundNumber === roundNumber,
    )
    const roundVariantIds =
      round?.variations.map((variation) => variation.variantId) ?? []

    setSelectedVariantIds((current) => {
      const next = new Set(current)
      roundVariantIds.forEach((variantId) => {
        if (checked) {
          next.add(variantId)
        } else {
          next.delete(variantId)
        }
      })
      return [...next]
    })
  }

  const handleToggleVariation = (variantId: string, checked: boolean) => {
    setSelectedVariantIds((current) => {
      const next = new Set(current)
      if (checked) {
        next.add(variantId)
      } else {
        next.delete(variantId)
      }
      return [...next]
    })
  }

  const handleExportRun = () => {
    if (!activeRunVariations.length) return
    downloadCsvString(
      runCsvFileName(activeRun),
      runVariationsToCsv(activeRunVariations, variantPreview),
    )
  }

  const handleGenerate = async () => {
    if (!domainIds.length || !selectedVersion || !selectedVariantIds.length) {
      return
    }

    setErrorMessage("")
    setPendingAction("generate")
    let done = 0
    let total = selectedVariantIds.length

    try {
      const run = await createSpamTest({
        id: sequence._id,
        body: {
          domainIds,
          emailIndex,
          versionIndex,
          recipientProvider,
          selectedVariantIds,
        },
      }).unwrap()
      const runId = spamTestRunId(run)

      if (!runId) {
        throw new Error("missing spam test run id")
      }

      setSelectedRunId(runId)
      total = run.selectedVariantIds?.length ?? selectedVariantIds.length
      done = run.variations?.length ?? done
      setGenerateProgress({ done, total })

      let finished = run.status === "generated" || done >= total
      let generationFailed = false

      while (!finished) {
        let response: SequenceSpamTestRun | undefined

        for (let attempt = 1; attempt <= maxGenerateAttempts; attempt += 1) {
          try {
            response = await generateNext({ id: sequence._id, runId }).unwrap()
            break
          } catch {
            if (attempt === maxGenerateAttempts) {
              generationFailed = true
              setErrorMessage(
                `Unable to generate variations (failed on step ${
                  done + 1
                } of ${total}). Please try again.`,
              )
              break
            }

            await delay(generateRetryDelayMs)
          }
        }

        if (generationFailed || !response) {
          setPollRuns(false)
          setPollRun(false)
          break
        }

        const responseRunId = spamTestRunId(response)
        if (responseRunId) {
          setSelectedRunId(responseRunId)
        }

        done = response.variations?.length ?? done
        setGenerateProgress({ done, total })
        finished = response.status === "generated" || done >= total
      }

      if (!generationFailed) {
        setPollRuns(false)
        setPollRun(false)
        setGenerateProgress(null)
      }
    } catch {
      setErrorMessage("Unable to generate variations. Please try again.")
      setPollRuns(false)
      setPollRun(false)
    } finally {
      setPendingAction(null)
    }
  }

  const handleTest = async () => {
    if (!domainIds.length || !testRunId) {
      return
    }

    setErrorMessage("")
    setPendingAction("test")

    try {
      const run = await testSpamTest({
        id: sequence._id,
        runId: testRunId,
        body: {
          domainIds,
          recipientProvider,
        },
      }).unwrap()
      const runId = spamTestRunId(run)

      if (runId) {
        setSelectedRunId(runId)
      }

      setPollRuns(true)
      setPollRun(true)
    } catch {
      setErrorMessage("Unable to start spam test. Please try again.")
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <div className={cx("flex flex-col gap-5", className)}>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Select
          mode="multiple"
          value={domainIds}
          onChange={(value) => setDomainIds(value as string[])}
          showSearch
          filterOption={filterOption}
          options={domains.map(({ _id, name, emailProvider }) => ({
            label: `${name}${emailProvider ? ` (${emailProvider})` : ""}`,
            value: _id,
          }))}
          loading={domainsLoading || domainsFetching}
          bordered={false}
          className="bg-input min-h-10 rounded-md"
          label="Domains"
          placeholder="Select domains"
          getPopupContainer={(node) => node.parentNode}
        />
        <Select
          value={emailIndex}
          onChange={(value) => {
            setEmailIndex(Number(value))
            setVersionIndex(0)
          }}
          options={sequence.emails
            .slice(0, 1)
            .map(({ description }, index) => ({
              label: description || `Email ${index + 1}`,
              value: index,
            }))}
          disabled
          bordered={false}
          className="bg-input h-10 rounded-md"
          label="Email (first email only)"
          placeholder="Select email"
          getPopupContainer={(node) => node.parentNode}
        />
        <Select
          value={versionIndex}
          onChange={(value) => setVersionIndex(Number(value))}
          options={(selectedEmail?.versions || []).map((_, index) => ({
            label: `Version ${index + 1}`,
            value: index,
          }))}
          bordered={false}
          className="bg-input h-10 rounded-md"
          label="Version"
          placeholder="Select version"
          getPopupContainer={(node) => node.parentNode}
        />
        <Select
          value={recipientProvider}
          onChange={(value) =>
            setRecipientProvider(value as SequenceSpamTestRecipientProvider)
          }
          options={providerOptions}
          bordered={false}
          className="bg-input h-10 rounded-md"
          label="Provider"
          placeholder="Select provider"
          getPopupContainer={(node) => node.parentNode}
        />
      </div>

      <div className="rounded-md border border-outline bg-input/40 p-4 flex flex-col gap-1">
        <h3 className="text-base font-semibold">
          Seed results per variation: {seedsPerVariation || "—"}
        </h3>
        <p className="text-sm leading-6 text-black/70">
          {selectedDomains.length ? (
            <>
              Seed results are measured for{" "}
              <span className="font-medium text-black">
                {selectedDomains
                  .map(({ name }) => name)
                  .filter(Boolean)
                  .join(", ")}
              </span>
              .
            </>
          ) : (
            "Select one or more domains to seed and measure placement."
          )}
        </p>
        {selectedDomains.length ? (
          <p className="text-sm leading-6 text-black/60">
            Estimated {estimatedSeedSends} seed send
            {estimatedSeedSends === 1 ? "" : "s"} across{" "}
            {selectedDomains.length} domain
            {selectedDomains.length === 1 ? "" : "s"} ({selectedDomainsCapacity}{" "}
            available daily sends).
          </p>
        ) : null}
      </div>

      <div className="rounded-md border border-outline p-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Variations to generate</h3>
            <p className="text-sm leading-6 text-black/60">
              Select only the round or variations you want to test. Round 1 is
              selected by default to avoid using seed capacity unnecessarily.
            </p>
          </div>
          {(optionsLoading || optionsFetching) && (
            <span className="text-xs text-primary">Loading...</span>
          )}
        </div>

        {variationRounds.length ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            {variationRounds.map((round) => {
              const roundVariantIds = round.variations.map(
                (variation) => variation.variantId,
              )
              const selectedInRound = roundVariantIds.filter((variantId) =>
                selectedVariantIdSet.has(variantId),
              ).length
              const roundChecked =
                selectedInRound > 0 &&
                selectedInRound === roundVariantIds.length
              const roundIndeterminate =
                selectedInRound > 0 && selectedInRound < roundVariantIds.length

              return (
                <div
                  key={round.roundNumber}
                  className="rounded-md bg-input p-3 flex flex-col gap-3"
                >
                  <label className="flex items-center gap-2 font-semibold">
                    <Checkbox
                      checked={roundChecked}
                      indeterminate={roundIndeterminate}
                      onChange={({ target: { checked } }) =>
                        handleToggleRound(round.roundNumber, checked)
                      }
                    />
                    Round {round.roundNumber}
                  </label>
                  <div className="flex flex-col gap-2">
                    {round.variations.map((variation) => (
                      <label
                        key={variation.variantId}
                        className="flex items-start gap-2 text-sm leading-5"
                      >
                        <Checkbox
                          checked={selectedVariantIdSet.has(
                            variation.variantId,
                          )}
                          onChange={({ target: { checked } }) =>
                            handleToggleVariation(variation.variantId, checked)
                          }
                        />
                        <span>
                          <span className="font-medium">
                            {variation.variationName || variation.variantId}
                          </span>
                          <span className="block text-xs text-black/50">
                            {formatChangedFields(variation.changedFields)}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-sm leading-6 text-black/60">
            No variation options are available.
          </div>
        )}

        <div className="text-sm leading-6 text-black/70">
          Selected {selectedVariationCount} variation
          {selectedVariationCount === 1 ? "" : "s"} to generate.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row gap-3">
          <OutlineButton
            type="button"
            className="btn-sm text-black"
            disabled={disableGenerate}
            loading={pendingAction === "generate"}
            onClick={handleGenerate}
          >
            Generate Variations
          </OutlineButton>
          <Button
            type="button"
            className="btn-sm"
            disabled={disableTest}
            loading={pendingAction === "test"}
            onClick={handleTest}
          >
            Test on Seeds
          </Button>
          {pendingAction === "generate" && generateProgress ? (
            <span className="text-xs leading-5 text-primary">
              Generating {generateProgress.done}/{generateProgress.total}…
            </span>
          ) : null}
        </div>
        <p className="text-xs leading-5 text-black/50">
          Generate variations first, then run Test on Seeds against the selected
          run. Each test creates a new run so you can re-test the same copy and
          compare history below.
        </p>
      </div>

      {errorMessage && (
        <ErrorMessage className="bg-error/10" message={errorMessage} />
      )}

      <div className="rounded-md border border-outline p-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <h3 className="text-base font-semibold">Status</h3>
          <div className="flex items-center gap-2">
            {(runsFetching || runFetching) && (
              <span className="text-xs text-primary">Refreshing...</span>
            )}
            <Select
              value={activeRunId || null}
              onChange={(value) => setSelectedRunId(value as string)}
              options={spamTestRuns.map((run) => ({
                label: runHistoryLabel(run),
                value: spamTestRunId(run) || "",
              }))}
              loading={runsLoading || runsFetching}
              disabled={!spamTestRuns.length}
              bordered={false}
              className="bg-input h-10 rounded-md min-w-[260px]"
              label="Run history"
              placeholder="Select a run"
              getPopupContainer={(node) => node.parentNode}
            />
          </div>
        </div>
        <div className="text-sm leading-6 text-black/70">
          {runsLoading || runLoading ? (
            <Loader className="!w-6" />
          ) : activeRun ? (
            <span>
              Selected spam test is{" "}
              <span className="font-semibold text-black">
                {activeRunStatus}
              </span>
              {activeRunIsTest
                ? ` with ${activeRunSummary.pending} pending seed results.`
                : "."}
            </span>
          ) : (
            <span>No spam test has been generated for this sequence yet.</span>
          )}
        </div>
        {activeRunDomainNames.length ? (
          <div className="text-sm leading-6 text-black/70">
            Seed results for:{" "}
            <span className="font-medium text-black">
              {activeRunDomainNames.join(", ")}
            </span>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {countKeys.map((key) => (
          <div
            key={key}
            className="rounded-md border border-outline bg-white p-3 flex flex-col gap-1"
          >
            <span className="text-xs uppercase tracking-wide text-black/50">
              {countLabels[key]}
            </span>
            <span className="text-xl font-semibold">
              {activeRunSummary[key]}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-5">
        <div className="rounded-md border border-outline p-4 flex flex-col gap-3">
          <h3 className="text-base font-semibold">Copy Preview</h3>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-6 text-black bg-input rounded-md p-3 max-h-80 overflow-auto">
            {copyPreview || "No copy preview available."}
          </pre>
        </div>

        <div className="rounded-md border border-outline p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold">Generated Variations</h3>
            <OutlineButton
              type="button"
              className="btn-sm text-black"
              disabled={!activeRunVariations.length}
              onClick={handleExportRun}
              data-testid="export-run"
            >
              Export CSV
            </OutlineButton>
          </div>
          {activeRunVariations.length ? (
            <div className="flex flex-col gap-3">
              {activeRunVariations.map((variation, index) => {
                const preview = variantPreview(variation)
                const domainResults = variation.domainResults ?? []
                const seedBlocks = domainResults.length
                  ? domainResults.map((result) => ({
                      key:
                        result.domain?._id || result.domain?.name || "domain",
                      label: result.domain?.name || "Domain",
                      summary: spamTestSummary(result.summary),
                    }))
                  : variation.summary
                  ? [
                      {
                        key: "all",
                        label: "Seed results",
                        summary: spamTestSummary(variation.summary),
                      },
                    ]
                  : []

                return (
                  <div
                    key={variation._id || variation.id || index}
                    className="rounded-md bg-input p-3 text-sm leading-6"
                  >
                    <div className="font-semibold mb-2">
                      {variation.variationName || `Variation ${index + 1}`}
                    </div>
                    {(variation.subject || variation.copy?.subject) && (
                      <div className="mb-2">
                        <span className="font-medium">Subject:</span>{" "}
                        {variation.subject || variation.copy?.subject}
                      </div>
                    )}
                    {seedBlocks.length ? (
                      <div className="mb-3 flex flex-col gap-2">
                        {seedBlocks.map((block) => (
                          <div
                            key={block.key}
                            className="rounded bg-white/70 p-2"
                          >
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-black/50">
                              {block.label}
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                              {countKeys.map((key) => (
                                <div
                                  key={key}
                                  className="rounded border border-outline/70 bg-white px-2 py-1"
                                >
                                  <div className="text-[10px] uppercase tracking-wide text-black/50">
                                    {countLabels[key]}
                                  </div>
                                  <div className="text-sm font-semibold text-black">
                                    {block.summary[key]}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <pre className="whitespace-pre-wrap font-sans">
                      {preview || "No preview available."}
                    </pre>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-sm leading-6 text-black/60">
              No generated variations yet.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SequenceSpamTestPanel
